import { useCallback, useMemo, useState } from "react";

export interface ToastAction {
	label: string;
	onClick?: () => void;
}

export interface Toast {
	id: string;
	title: string;
	description: string;
	action?: ToastAction;
	variant?: "default" | "destructive";
	createdAt: number;
}

export interface ToastInput {
	title: string;
	description?: string;
	action?: ToastAction;
	variant?: "default" | "destructive";
}

export interface UseToastResult {
	toasts: Toast[];
	toast: (input: ToastInput) => string;
	dismiss: (id: string) => void;
}

function generateToastId(): string {
	if (
		typeof crypto !== "undefined" &&
		typeof crypto.randomUUID === "function"
	) {
		return crypto.randomUUID();
	}

	return `toast_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function useToast(): UseToastResult {
	const [toasts, setToasts] = useState<Toast[]>([]);

	const toast = useCallback((input: ToastInput) => {
		const id = generateToastId();
		const newToast: Toast = {
			id,
			title: input.title,
			description: input.description ?? "",
			action: input.action,
			variant: input.variant ?? "default",
			createdAt: Date.now(),
		};

		setToasts((prev) => [...prev, newToast]);

		return id;
	}, []);

	const dismiss = useCallback((id: string) => {
		setToasts((prev) => prev.filter((toastItem) => toastItem.id !== id));
	}, []);

	return useMemo(
		() => ({
			toasts,
			toast,
			dismiss,
		}),
		[toasts, toast, dismiss],
	);
}
